import { useRef, useState } from "react";
import { UploadCloud, X } from "lucide-react";
import api, { ASSET_URL } from "@/lib/api";

const ACCEPTED = ["image/png", "image/jpeg", "image/svg+xml", "image/webp"];
const MAX_BYTES = 5 * 1024 * 1024; // 5 MB

/**
 * Logo upload + live preview — the critical B2B feature.
 * `value` is { logo_filename, logo_url, logo_text }; `onChange` receives updates.
 */
export default function LogoUpload({ value, onChange }) {
  const branding = value || {};
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const handleFile = async (file) => {
    setError("");
    if (!file) return;
    if (!ACCEPTED.includes(file.type)) {
      setError("Logo must be a PNG, JPG, SVG, or WebP image.");
      return;
    }
    if (file.size > MAX_BYTES) {
      setError("Logo file must be under 5 MB.");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post("/uploads/logo", form);
      onChange({
        ...branding,
        logo_filename: res.data.filename,
        logo_url: res.data.url,
      });
    } catch {
      setError("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const removeLogo = () =>
    onChange({ ...branding, logo_filename: null, logo_url: null });

  return (
    <div className="space-y-4">
      <div>
        <label className="label">Logo / business name (text)</label>
        <input
          className="input"
          placeholder="e.g. Riverbend Charity Classic"
          value={branding.logo_text || ""}
          onChange={(e) => onChange({ ...branding, logo_text: e.target.value })}
        />
      </div>

      <div>
        <label className="label">Logo image</label>
        {branding.logo_url ? (
          <div className="relative inline-block">
            <img
              src={`${ASSET_URL}${branding.logo_url}`}
              alt="Uploaded logo preview"
              className="h-28 w-28 rounded-lg border border-gray-200 bg-white object-contain p-2"
            />
            <button
              type="button"
              onClick={removeLogo}
              className="absolute -right-2 -top-2 rounded-full bg-gray-800 p-1 text-white"
              aria-label="Remove logo"
            >
              <X size={14} />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="flex h-28 w-full flex-col items-center justify-center gap-1
              rounded-lg border-2 border-dashed border-gray-300 text-sm text-gray-500
              hover:border-fairway-500 hover:text-fairway-700"
          >
            <UploadCloud size={22} />
            {uploading ? "Uploading…" : "Click to upload your logo"}
            <span className="text-xs text-gray-400">PNG, JPG, SVG, WebP · max 5 MB</span>
          </button>
        )}
        <input
          ref={fileRef}
          type="file"
          accept={ACCEPTED.join(",")}
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}

      {/* Live preview — logo mocked onto the kit. */}
      <div>
        <label className="label">Preview on kit</label>
        <div className="flex h-40 items-center justify-center rounded-lg bg-fairway-50">
          <div className="relative flex h-32 w-24 flex-col items-center justify-center rounded-md border-2 border-fairway-600 bg-white">
            {branding.logo_url ? (
              <img
                src={`${ASSET_URL}${branding.logo_url}`}
                alt="Logo on kit"
                className="max-h-14 max-w-[80%] object-contain"
              />
            ) : (
              <span className="px-1 text-center text-[10px] text-gray-400">
                Your logo here
              </span>
            )}
            {branding.logo_text && (
              <span className="mt-1 px-1 text-center text-[9px] font-semibold text-fairway-700">
                {branding.logo_text}
              </span>
            )}
            <span className="absolute -top-2 h-3 w-6 rounded-t bg-fairway-600" />
          </div>
        </div>
      </div>
    </div>
  );
}
